// components/CaracteristicasJugador.js

import React from 'react';
import { useTranslation } from 'react-i18next';
import { useClubTheme } from '../../contexts/ClubThemeContext';
import { legacyToKeyMap, normalizeRoleValue, normalizeArrayValues } from '../../utils/legacyMapping';
import {
  Paper,
  Typography,
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Checkbox,
  ListItemText,
  ListSubheader
} from '@mui/material';
import { Clear } from '@mui/icons-material';

// Helper: header de groupe cliquable pour les Select multi
const GroupHeader = ({ label, groupRoles, currentValue, field, setEditablePlayer }) => {
  const currentRoles = currentValue ? currentValue.split(',').filter(Boolean) : [];
  const allChecked = groupRoles.every(r => currentRoles.includes(r));
  const someChecked = groupRoles.some(r => currentRoles.includes(r));
  return (
    <ListSubheader
      sx={{
        display: 'flex', alignItems: 'center', fontWeight: 600, fontSize: '0.95rem',
        cursor: 'pointer', lineHeight: '40px', '&:hover': { backgroundColor: 'action.hover' }
      }}
      onClick={() => {
        const updated = allChecked
          ? currentRoles.filter(r => !groupRoles.includes(r))
          : [...new Set([...currentRoles, ...groupRoles])];
        setEditablePlayer(prev => ({ ...prev, [field]: updated.filter(Boolean).join(',') }));
      }}
    >
      <Checkbox checked={allChecked} indeterminate={someChecked && !allChecked} size="small" />
      {label}
    </ListSubheader>
  );
};

// === Arrays de roles y atributos (using translation keys) ===
const offensiveRoles = [
  // Guard
  'ballHandler',
  'secondaryBallHandler',
  'dominantScorer',
  'floorGeneral',
  'shootingSpecialist',

  // Wing
  'creatorWing',
  'versatileWing',
  'strongWing',
  'slasherWing',
  'spotUpWing',
  'threeAndD',

  // Forward
  'traditionalPowerForward',
  'skilledForward',
  'stretchFour',

  // Big
  'creatorCenter',
  'versatileCenter',
  'midRangeCenter',
  'stretchFive',
  'postScorer',
  'rimFinisher',
  'pickAndRollBig',
  'marginalOffensiveRole'
];

const defensiveRoles = [
  'pointOfAttack',
  'intenseOneOnOne',
  'perimeterDefender',
  'wingStopper',
  'switchDefender',
  'anchorBig',
  'defensiveRebounder',
  'rimProtector',
  'slowLaterally',
  'nonDefender'
];

const JEAttributes = [
  'oneOnOne',
  'threeLevelScorer',
  'offBallActivity',
  'athletic',
  'pickAndRollAbility',
  'foulDrawing',
  'clutch',
  'tough',
  'energy',
  'wingspan',
  'physical',
  'hustle',
  'bbiq',
  'postGame',
  'paintTouches',
  'slasher',
  'scoring',
  'firstStep',
  'consistentProduction',
  'rebounding',
  'talent',
  'shooter',
  'catchAndShoot',
  'cornerThree',
  'pullUpShooting',
  'transition',
  'blueCollar',
  'speedAgility',
  'versatility',
  'verticality'
];

const JIAttributes = [
  'oneOnOne',
  'offBallActivity',
  'athletic',
  'screenAndRoll',
  'effectiveScreens',
  'softTouch',
  'clutch',
  'tough',
  'energy',
  'wingspan',
  'footwork',
  'physical',
  'hustle',
  'bbiq',
  'postUpFaceUp',
  'lobThreat',
  'scoring',
  'consistentProduction',
  'rimProtection',
  'rebounding',
  'rimRunner',
  'talent',
  'shooter',
  'catchAndShoot',
  'cornerThreePopOut',
  'pullUpShooting',
  'blueCollar',
  'transition',
  'multipleEfforts',
  'speedAgility',
  'versatility',
  'verticality'
];

// Opciones para los ratings (using translation keys)
const vbcRatings = [
  'lowLevelProspect',
  'midLevelProspect',
  'goodLevelProspect',
  'topProspect',
  'futureStar',
  'ebaLow',
  'ebaHigh',
  'lebLow',
  'lebHigh',
  'acbRotation',
  'bottomBclEurocup',
  'midBclEurocup',
  'topBclEurocup',
  'elMid',
  'elTop',
  'nbaRotation',
  'nbaAllStar',
  'collegeLow',
  'collegeMid',
  'collegeTop'
];

// === Fonction utilitaire pour tronquer le texte (si nécessaire) ===
const truncateText = (text, maxLength = 30) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '...';
};

function CaracteristicasJugador({
  player,
  editablePlayer,
  setEditablePlayer,
  isEditing,
  isMobile,
  handleChange
}) {
  const { t } = useTranslation();
  const { clubName } = useClubTheme();

  // Normaliser les anciennes valeurs espagnoles vers les clés de traduction au chargement
  React.useEffect(() => {
    if (isEditing && editablePlayer) {
      const updates = {};
      let needsUpdate = false;
      // Champs CSV (string séparé par virgules)
      ['rolOfensivo', 'rolDefensivo'].forEach(field => {
        if (editablePlayer[field]) {
          const normalized = normalizeRoleValue(editablePlayer[field]);
          if (normalized !== editablePlayer[field]) {
            updates[field] = normalized;
            needsUpdate = true;
          }
        }
      });
      // Champs array
      if (Array.isArray(editablePlayer.jeJiAttributes) && editablePlayer.jeJiAttributes.length > 0) {
        const normalized = normalizeArrayValues(editablePlayer.jeJiAttributes);
        if (JSON.stringify(normalized) !== JSON.stringify(editablePlayer.jeJiAttributes)) {
          updates.jeJiAttributes = normalized;
          needsUpdate = true;
        }
      }
      if (needsUpdate) {
        setEditablePlayer(prev => ({ ...prev, ...updates }));
      }
    }
  }, [isEditing]);

  // Fonction pour vider un champ (pour les single selects)
  const clearSelectValue = (e, fieldName) => {
    e.stopPropagation();
    setEditablePlayer((prev) => ({
      ...prev,
      [fieldName]: ''
    }));
  };

  // === RENDER MOBILE ===
  if (isMobile) {
    return (
      <Box
        sx={{
          mb: 2,
          backgroundColor: 'background.default',
          // Empêche le dépassement horizontal sur mobile
          width: '100%',
          maxWidth: '100%',
          overflowX: 'auto'
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 'medium',
            px: 2,
            py: 1,
            backgroundColor: 'background.paper',
            // Force le retour à la ligne si besoin
            whiteSpace: 'normal',
            wordBreak: 'break-word'
          }}
        >
          2. {t('createPlayer.sections.playerCharacteristics')}
        </Typography>

        <Box
          sx={{
            p: 2,
            // Forcer les champs de texte et Select à couper les mots si besoin
            '& .MuiInputBase-input': {
              whiteSpace: 'normal',
              wordBreak: 'break-word'
            }
          }}
        >
          {isEditing ? (
            <Grid container spacing={2} sx={{ maxWidth: '100%' }}>
              {/* Posición */}
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>{t('createPlayer.fields.position')}</InputLabel>
                  <Select
                    name="poste"
                    value={editablePlayer.poste || ''}
                    onChange={handleChange}
                    label={t('createPlayer.fields.position')}
                    renderValue={(selected) => {
                      const positionMap = {
                        'Base': t('positions.pointGuard'),
                        'Escolta': t('positions.shootingGuard'),
                        'Alero': t('positions.smallForward'),
                        'Ala-Pívot': t('positions.powerForward'),
                        'Pívot': t('positions.center')
                      };
                      return positionMap[selected] || selected;
                    }}
                    endAdornment={
                      editablePlayer.poste && (
                        <IconButton
                          onClick={(e) => clearSelectValue(e, 'poste')}
                          sx={{ position: 'absolute', right: 32 }}
                          size="small"
                        >
                          <Clear fontSize="small" />
                        </IconButton>
                      )
                    }
                  >
                    <MenuItem value="Base">{t('positions.pointGuard')}</MenuItem>
                    <MenuItem value="Escolta">{t('positions.shootingGuard')}</MenuItem>
                    <MenuItem value="Alero">{t('positions.smallForward')}</MenuItem>
                    <MenuItem value="Ala-Pívot">{t('positions.powerForward')}</MenuItem>
                    <MenuItem value="Pívot">{t('positions.center')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Mano Dominante */}
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>{t('createPlayer.fields.dominantHand')}</InputLabel>
                  <Select
                    name="mano"
                    value={editablePlayer.mano || ''}
                    onChange={handleChange}
                    label={t('createPlayer.fields.dominantHand')}
                    endAdornment={
                      editablePlayer.mano && (
                        <IconButton
                          onClick={(e) => clearSelectValue(e, 'mano')}
                          sx={{ position: 'absolute', right: 32 }}
                          size="small"
                        >
                          <Clear fontSize="small" />
                        </IconButton>
                      )
                    }
                    renderValue={(selected) => t(`createPlayer.fields.${selected}`, selected)}
                  >
                    <MenuItem value="rightHand">{t('createPlayer.fields.rightHand')}</MenuItem>
                    <MenuItem value="leftHand">{t('createPlayer.fields.leftHand')}</MenuItem>
                    <MenuItem value="bothHands">{t('createPlayer.fields.bothHands')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Categoría */}
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>{t('createPlayer.fields.category')}</InputLabel>
                  <Select
                    name="categoria"
                    value={editablePlayer.categoria || ''}
                    onChange={handleChange}
                    label={t('createPlayer.fields.category')}
                    renderValue={(selected) => t(`createPlayer.categories.${selected}`, selected)}
                    endAdornment={
                      editablePlayer.categoria && (
                        <IconButton
                          onClick={(e) => clearSelectValue(e, 'categoria')}
                          sx={{ position: 'absolute', right: 32 }}
                          size="small"
                        >
                          <Clear fontSize="small" />
                        </IconButton>
                      )
                    }
                  >
                    <MenuItem value="handler">{t('createPlayer.categories.handler')}</MenuItem>
                    <MenuItem value="exterior">{t('createPlayer.categories.exterior')}</MenuItem>
                    <MenuItem value="interior">{t('createPlayer.categories.interior')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Shooting Range */}
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>{t('createPlayer.fields.shootingRange')}</InputLabel>
                  <Select
                    name="shootingRange"
                    value={editablePlayer.shootingRange || ''}
                    onChange={handleChange}
                    label={t('createPlayer.fields.shootingRange')}
                    endAdornment={
                      editablePlayer.shootingRange && (
                        <IconButton
                          onClick={(e) => clearSelectValue(e, 'shootingRange')}
                          sx={{ position: 'absolute', right: 32 }}
                          size="small"
                        >
                          <Clear fontSize="small" />
                        </IconButton>
                      )
                    }
                  >
                    <MenuItem value="Rim">{t('createPlayer.fields.rim')}</MenuItem>
                    <MenuItem value="Mid-Range">{t('createPlayer.fields.midRange')}</MenuItem>
                    <MenuItem value="3Pts">{t('createPlayer.fields.threePoints')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Perfil Ofensivo (MULTI SELECT) */}
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>{t('createPlayer.fields.offensiveProfile')}</InputLabel>
                  <Select
                    multiple
                    name="rolOfensivo"
                    value={
                      editablePlayer.rolOfensivo
                        ? editablePlayer.rolOfensivo.split(',')
                        : []
                    }
                    onChange={(e) => {
                      setEditablePlayer((prev) => ({
                        ...prev,
                        rolOfensivo: e.target.value.join(',')
                      }));
                    }}
                    // --- On tronque l'affichage dans renderValue ---
                    renderValue={(selected) => {
                      const translatedRoles = selected.map(role => t(`createPlayer.offensiveRoles.${role}`, role));
                      const joined = translatedRoles.join(', ');
                      return (
                        <Box
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {truncateText(joined, 30)}
                        </Box>
                      );
                    }}
                  >
                    <GroupHeader label="Guard" groupRoles={offensiveRoles.slice(0, 5)} currentValue={editablePlayer.rolOfensivo} field="rolOfensivo" setEditablePlayer={setEditablePlayer} />
                    {offensiveRoles.slice(0, 5).map((role) => (
                      <MenuItem key={role} value={role} sx={{ pl: 4 }}>
                        <Checkbox checked={editablePlayer.rolOfensivo && editablePlayer.rolOfensivo.split(',').includes(role)} />
                        <ListItemText primary={t(`createPlayer.offensiveRoles.${role}`, role)} />
                      </MenuItem>
                    ))}

                    <GroupHeader label="Wing" groupRoles={offensiveRoles.slice(5, 11)} currentValue={editablePlayer.rolOfensivo} field="rolOfensivo" setEditablePlayer={setEditablePlayer} />
                    {offensiveRoles.slice(5, 11).map((role) => (
                      <MenuItem key={role} value={role} sx={{ pl: 4 }}>
                        <Checkbox checked={editablePlayer.rolOfensivo && editablePlayer.rolOfensivo.split(',').includes(role)} />
                        <ListItemText primary={t(`createPlayer.offensiveRoles.${role}`, role)} />
                      </MenuItem>
                    ))}

                    <GroupHeader label="Forward" groupRoles={offensiveRoles.slice(11, 14)} currentValue={editablePlayer.rolOfensivo} field="rolOfensivo" setEditablePlayer={setEditablePlayer} />
                    {offensiveRoles.slice(11, 14).map((role) => (
                      <MenuItem key={role} value={role} sx={{ pl: 4 }}>
                        <Checkbox checked={editablePlayer.rolOfensivo && editablePlayer.rolOfensivo.split(',').includes(role)} />
                        <ListItemText primary={t(`createPlayer.offensiveRoles.${role}`, role)} />
                      </MenuItem>
                    ))}

                    <GroupHeader label="Big" groupRoles={offensiveRoles.slice(14)} currentValue={editablePlayer.rolOfensivo} field="rolOfensivo" setEditablePlayer={setEditablePlayer} />
                    {offensiveRoles.slice(14).map((role) => (
                      <MenuItem key={role} value={role} sx={{ pl: 4 }}>
                        <Checkbox checked={editablePlayer.rolOfensivo && editablePlayer.rolOfensivo.split(',').includes(role)} />
                        <ListItemText primary={t(`createPlayer.offensiveRoles.${role}`, role)} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Perfil Defensivo (MULTI SELECT) */}
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>{t('createPlayer.fields.defensiveProfile')}</InputLabel>
                  <Select
                    multiple
                    name="rolDefensivo"
                    value={
                      editablePlayer.rolDefensivo
                        ? editablePlayer.rolDefensivo.split(',')
                        : []
                    }
                    onChange={(e) => {
                      setEditablePlayer((prev) => ({
                        ...prev,
                        rolDefensivo: e.target.value.join(',')
                      }));
                    }}
                    renderValue={(selected) => {
                      const translatedRoles = selected.map(role => t(`createPlayer.defensiveRoles.${role}`, role));
                      const joined = translatedRoles.join(', ');
                      return (
                        <Box
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {truncateText(joined, 30)}
                        </Box>
                      );
                    }}
                  >
                    {defensiveRoles.map((role) => (
                      <MenuItem key={role} value={role}>
                        <Checkbox
                          checked={
                            editablePlayer.rolDefensivo &&
                            editablePlayer.rolDefensivo.split(',').includes(role)
                          }
                        />
                        <ListItemText primary={t(`createPlayer.defensiveRoles.${role}`, role)} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* JE/JI */}
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>{t('createPlayer.fields.skillsJeJi')}</InputLabel>
                  <Select
                    name="jeJiType"
                    value={editablePlayer.jeJiType || ''}
                    onChange={(e) => {
                      // si cambia el tipo, reseteamos los atributos
                      setEditablePlayer((prev) => ({
                        ...prev,
                        jeJiType: e.target.value,
                        jeJiAttributes: []
                      }));
                    }}
                    label={t('createPlayer.fields.skillsJeJi')}
                    endAdornment={
                      editablePlayer.jeJiType && (
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditablePlayer((prev) => ({
                              ...prev,
                              jeJiType: '',
                              jeJiAttributes: []
                            }));
                          }}
                          sx={{ position: 'absolute', right: 32 }}
                          size="small"
                        >
                          <Clear fontSize="small" />
                        </IconButton>
                      )
                    }
                  >
                    <MenuItem value="JE">{t('createPlayer.fields.jeExterior')}</MenuItem>
                    <MenuItem value="JI">{t('createPlayer.fields.jiInterior')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Skills JE/JI (MULTI SELECT) */}
              {editablePlayer.jeJiType && (
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>
                      {`Skills ${
                        editablePlayer.jeJiType === 'JE' ? 'Exterior' : 'Interior'
                      }`}
                    </InputLabel>
                    <Select
                      multiple
                      value={editablePlayer.jeJiAttributes || []}
                      onChange={(e) =>
                        setEditablePlayer((prev) => ({
                          ...prev,
                          jeJiAttributes: e.target.value
                        }))
                      }
                      renderValue={(selected) => {
                        const attributeType = editablePlayer.jeJiType === 'JE' ? 'jeAttributes' : 'jiAttributes';
                        const translatedAttrs = selected.map(attr => t(`createPlayer.${attributeType}.${attr}`, attr));
                        const joined = translatedAttrs.join(', ');
                        return (
                          <Box
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {truncateText(joined, 30)}
                          </Box>
                        );
                      }}
                    >
                      {(editablePlayer.jeJiType === 'JE'
                        ? JEAttributes
                        : JIAttributes
                      ).map((attr) => {
                        const attributeType = editablePlayer.jeJiType === 'JE' ? 'jeAttributes' : 'jiAttributes';
                        return (
                          <MenuItem key={attr} value={attr}>
                            <Checkbox
                              checked={editablePlayer.jeJiAttributes.includes(attr)}
                            />
                            <ListItemText primary={t(`createPlayer.${attributeType}.${attr}`, attr)} />
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              {/* Current VBC Rating */}
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>{t('createPlayer.fields.currentVbcRating', { clubName })}</InputLabel>
                  <Select
                    name="nivel"
                    value={editablePlayer.nivel || ''}
                    onChange={handleChange}
                    label={t('createPlayer.fields.currentVbcRating', { clubName })}
                    renderValue={(selected) => t(`createPlayer.levels.${selected}`, selected)}
                    endAdornment={
                      editablePlayer.nivel && (
                        <IconButton
                          onClick={(e) => clearSelectValue(e, 'nivel')}
                          sx={{ position: 'absolute', right: 32 }}
                          size="small"
                        >
                          <Clear fontSize="small" />
                        </IconButton>
                      )
                    }
                  >
                    {vbcRatings.map((rating) => (
                      <MenuItem key={rating} value={rating}>
                        {t(`createPlayer.levels.${rating}`, rating)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Proyección VBC Rating */}
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>{t('createPlayer.fields.projectionVbcRating', { clubName })}</InputLabel>
                  <Select
                    name="proyeccion"
                    value={editablePlayer.proyeccion || ''}
                    onChange={handleChange}
                    label={t('createPlayer.fields.projectionVbcRating', { clubName })}
                    renderValue={(selected) => t(`createPlayer.levels.${selected}`, selected)}
                    endAdornment={
                      editablePlayer.proyeccion && (
                        <IconButton
                          onClick={(e) => clearSelectValue(e, 'proyeccion')}
                          sx={{ position: 'absolute', right: 32 }}
                          size="small"
                        >
                          <Clear fontSize="small" />
                        </IconButton>
                      )
                    }
                  >
                    {vbcRatings.map((rating) => (
                      <MenuItem key={rating} value={rating}>
                        {t(`createPlayer.levels.${rating}`, rating)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Flag Behaviour, Injury Prone */}
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>{t('createPlayer.fields.flagBehaviour')}</InputLabel>
                  <Select
                    name="comportamiento"
                    value={editablePlayer.comportamiento || ''}
                    onChange={handleChange}
                    label={t('createPlayer.fields.flagBehaviour')}
                    endAdornment={
                      editablePlayer.comportamiento && (
                        <IconButton
                          onClick={(e) => clearSelectValue(e, 'comportamiento')}
                          sx={{ position: 'absolute', right: 32 }}
                          size="small"
                        >
                          <Clear fontSize="small" />
                        </IconButton>
                      )
                    }
                  >
                    <MenuItem value="Green">{t('createPlayer.fields.green')}</MenuItem>
                    <MenuItem value="Orange">{t('createPlayer.fields.orange')}</MenuItem>
                    <MenuItem value="Red">{t('createPlayer.fields.red')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>{t('createPlayer.fields.injuryProne')}</InputLabel>
                  <Select
                    name="injury"
                    value={editablePlayer.injury || ''}
                    onChange={handleChange}
                    label={t('createPlayer.fields.injuryProne')}
                    endAdornment={
                      editablePlayer.injury && (
                        <IconButton
                          onClick={(e) => clearSelectValue(e, 'injury')}
                          sx={{ position: 'absolute', right: 32 }}
                          size="small"
                        >
                          <Clear fontSize="small" />
                        </IconButton>
                      )
                    }
                  >
                    <MenuItem value="Green">{t('createPlayer.fields.green')}</MenuItem>
                    <MenuItem value="Orange">{t('createPlayer.fields.orange')}</MenuItem>
                    <MenuItem value="Red">{t('createPlayer.fields.red')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>{t('createPlayer.fields.digitalBehaviour')}</InputLabel>
                  <Select
                    name="digital_behaviour"
                    value={editablePlayer.digital_behaviour || ''}
                    onChange={handleChange}
                    label={t('createPlayer.fields.digitalBehaviour')}
                    endAdornment={
                      editablePlayer.digital_behaviour && (
                        <IconButton
                          onClick={(e) => clearSelectValue(e, 'digital_behaviour')}
                          sx={{ position: 'absolute', right: 32 }}
                          size="small"
                        >
                          <Clear fontSize="small" />
                        </IconButton>
                      )
                    }
                  >
                    <MenuItem value="Green">{t('createPlayer.fields.green')}</MenuItem>
                    <MenuItem value="Orange">{t('createPlayer.fields.orange')}</MenuItem>
                    <MenuItem value="Red">{t('createPlayer.fields.red')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Comparación Pro */}
              <Grid item xs={12}>
                <TextField
                  label={t('createPlayer.fields.proComparison')}
                  name="comparacionPro"
                  value={editablePlayer.comparacionPro || ''}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>
            </Grid>
          ) : (
            // READ ONLY (MOBILE)
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {!!player.poste && (() => {
                const positionMap = {
                  'Base': t('positions.pointGuard'),
                  'Escolta': t('positions.shootingGuard'),
                  'Alero': t('positions.smallForward'),
                  'Ala-Pívot': t('positions.powerForward'),
                  'Ala-Pivot': t('positions.powerForward'),
                  'Pívot': t('positions.center'),
                  'Pivot': t('positions.center')
                };
                const translatedPosition = positionMap[player.poste] || player.poste;
                return (
                  <Typography>
                    <strong>{t('createPlayer.fields.position')}:</strong> {translatedPosition}
                  </Typography>
                );
              })()}
              {!!player.mano && (() => {
                // Support both old Spanish values and new translation keys
                const handMap = {
                  'Derecha': t('createPlayer.fields.rightHand'),
                  'Izquierda': t('createPlayer.fields.leftHand'),
                  'Ambas': t('createPlayer.fields.bothHands'),
                  'rightHand': t('createPlayer.fields.rightHand'),
                  'leftHand': t('createPlayer.fields.leftHand'),
                  'bothHands': t('createPlayer.fields.bothHands')
                };
                const translatedHand = handMap[player.mano] || player.mano;
                return (
                  <Typography>
                    <strong>{t('createPlayer.fields.dominantHand')}:</strong> {translatedHand}
                  </Typography>
                );
              })()}
              {!!player.categoria && (() => {
                // Support both old Spanish values and new translation keys
                const categoryMap = {
                  'Manejador': t('createPlayer.categories.handler'),
                  'Exterior': t('createPlayer.categories.exterior'),
                  'Interior': t('createPlayer.categories.interior'),
                  'handler': t('createPlayer.categories.handler'),
                  'exterior': t('createPlayer.categories.exterior'),
                  'interior': t('createPlayer.categories.interior')
                };
                const translatedCategory = categoryMap[player.categoria] || player.categoria;
                return (
                  <Typography>
                    <strong>{t('createPlayer.fields.category')}:</strong> {translatedCategory}
                  </Typography>
                );
              })()}
              {!!player.shootingRange && (() => {
                const shootingRangeMap = {
                  'Rim': t('createPlayer.fields.rim'),
                  'Mid-Range': t('createPlayer.fields.midRange'),
                  '3Pts': t('createPlayer.fields.threePoints')
                };
                const translatedRange = shootingRangeMap[player.shootingRange] || player.shootingRange;
                return (
                  <Typography>
                    <strong>{t('createPlayer.fields.shootingRange')}:</strong> {translatedRange}
                  </Typography>
                );
              })()}
              {!!player.rolOfensivo && (() => {
                const roles = normalizeRoleValue(player.rolOfensivo).split(',').map(role => role.trim());
                const translatedRoles = roles.map(role => t(`createPlayer.offensiveRoles.${role}`, role)).join(', ');
                return (
                  <Typography>
                    <strong>{t('createPlayer.fields.offensiveProfile')}:</strong> {translatedRoles}
                  </Typography>
                );
              })()}
              {!!player.rolDefensivo && (() => {
                const roles = normalizeRoleValue(player.rolDefensivo).split(',').map(role => role.trim());
                const translatedRoles = roles.map(role => t(`createPlayer.defensiveRoles.${role}`, role)).join(', ');
                return (
                  <Typography>
                    <strong>{t('createPlayer.fields.defensiveProfile')}:</strong> {translatedRoles}
                  </Typography>
                );
              })()}
              {!!player.jeJiType && (
                <Typography>
                  <strong>{t('createPlayer.fields.skillsJeJi')}:</strong> {player.jeJiType}
                </Typography>
              )}
              {!!player.jeJiAttributes?.length && (() => {
                const attributeType = player.jeJiType === 'JE' ? 'jeAttributes' : 'jiAttributes';
                const translatedAttributes = player.jeJiAttributes.map(attr => {
                  const normalized = legacyToKeyMap[attr] || attr;
                  return t(`createPlayer.${attributeType}.${normalized}`, normalized);
                }).join(', ');
                return (
                  <Typography>
                    <strong>Skills&nbsp;({player.jeJiType}):</strong>{' '}
                    {translatedAttributes}
                  </Typography>
                );
              })()}
              {!!player.nivel && (() => {
                const translatedLevel = t(`createPlayer.levels.${player.nivel}`, player.nivel);
                return (
                  <Typography>
                    <strong>{t('createPlayer.fields.currentVbcRating', { clubName })}:</strong> {translatedLevel}
                  </Typography>
                );
              })()}
              {!!player.proyeccion && (() => {
                const translatedLevel = t(`createPlayer.levels.${player.proyeccion}`, player.proyeccion);
                return (
                  <Typography>
                    <strong>{t('createPlayer.fields.projectionVbcRating', { clubName })}:</strong> {translatedLevel}
                  </Typography>
                );
              })()}
              {!!player.comportamiento && (() => {
                const flagMap = {
                  'Green': t('createPlayer.fields.green'),
                  'Orange': t('createPlayer.fields.orange'),
                  'Red': t('createPlayer.fields.red')
                };
                const translatedFlag = flagMap[player.comportamiento] || player.comportamiento;
                return (
                  <Typography>
                    <strong>{t('createPlayer.fields.flagBehaviour')}:</strong> {translatedFlag}
                  </Typography>
                );
              })()}
              {!!player.injury && (() => {
                const flagMap = {
                  'Green': t('createPlayer.fields.green'),
                  'Orange': t('createPlayer.fields.orange'),
                  'Red': t('createPlayer.fields.red')
                };
                const translatedFlag = flagMap[player.injury] || player.injury;
                return (
                  <Typography>
                    <strong>{t('createPlayer.fields.injuryProne')}:</strong> {translatedFlag}
                  </Typography>
                );
              })()}
              {!!player.digital_behaviour && (() => {
                const flagMap = {
                  'Green': t('createPlayer.fields.green'),
                  'Orange': t('createPlayer.fields.orange'),
                  'Red': t('createPlayer.fields.red')
                };
                const translatedFlag = flagMap[player.digital_behaviour] || player.digital_behaviour;
                return (
                  <Typography>
                    <strong>{t('createPlayer.fields.digitalBehaviour')}:</strong> {translatedFlag}
                  </Typography>
                );
              })()}
              {!!player.comparacionPro && (
                <Typography>
                  <strong>{t('createPlayer.fields.proComparison')}:</strong>{' '}
                  {player.comparacionPro}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Box>
    );
  }

  // === RENDER DESKTOP ===
  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.5,
        mb: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        backgroundColor: 'background.paper'
      }}
    >
      <Typography
        variant="h6"
        gutterBottom
        sx={{
          fontSize: '1rem',
          fontWeight: 600,
          color: 'text.primary',
          letterSpacing: '-0.01em',
          mb: 1,
          pb: 0.5,
          borderBottom: '1px solid',
          borderColor: 'primary.main'
        }}
      >
        2. {t('createPlayer.sections.playerCharacteristics')}
      </Typography>

      {isEditing ? (
        <Grid container spacing={2}>
          {/* Posición */}
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>{t('createPlayer.fields.position')}</InputLabel>
              <Select
                name="poste"
                value={editablePlayer.poste || ''}
                onChange={handleChange}
                label={t('createPlayer.fields.position')}
                renderValue={(selected) => {
                  const positionMap = {
                    'Base': t('positions.pointGuard'),
                    'Escolta': t('positions.shootingGuard'),
                    'Alero': t('positions.smallForward'),
                    'Ala-Pívot': t('positions.powerForward'),
                    'Pívot': t('positions.center')
                  };
                  return positionMap[selected] || selected;
                }}
                endAdornment={
                  editablePlayer.poste && (
                    <IconButton
                      onClick={(e) => clearSelectValue(e, 'poste')}
                      sx={{ position: 'absolute', right: 32 }}
                      size="small"
                    >
                      <Clear fontSize="small" />
                    </IconButton>
                  )
                }
              >
                <MenuItem value="Base">{t('positions.pointGuard')}</MenuItem>
                <MenuItem value="Escolta">{t('positions.shootingGuard')}</MenuItem>
                <MenuItem value="Alero">{t('positions.smallForward')}</MenuItem>
                <MenuItem value="Ala-Pívot">{t('positions.powerForward')}</MenuItem>
                <MenuItem value="Pívot">{t('positions.center')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Mano Dominante */}
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>{t('createPlayer.fields.dominantHand')}</InputLabel>
              <Select
                name="mano"
                value={editablePlayer.mano || ''}
                onChange={handleChange}
                label={t('createPlayer.fields.dominantHand')}
                endAdornment={
                  editablePlayer.mano && (
                    <IconButton
                      onClick={(e) => clearSelectValue(e, 'mano')}
                      sx={{ position: 'absolute', right: 32 }}
                      size="small"
                    >
                      <Clear fontSize="small" />
                    </IconButton>
                  )
                }
                renderValue={(selected) => t(`createPlayer.fields.${selected}`, selected)}
              >
                <MenuItem value="rightHand">{t('createPlayer.fields.rightHand')}</MenuItem>
                <MenuItem value="leftHand">{t('createPlayer.fields.leftHand')}</MenuItem>
                <MenuItem value="bothHands">{t('createPlayer.fields.bothHands')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Categoría */}
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>{t('createPlayer.fields.category')}</InputLabel>
              <Select
                name="categoria"
                value={editablePlayer.categoria || ''}
                onChange={handleChange}
                label={t('createPlayer.fields.category')}
                renderValue={(selected) => t(`createPlayer.categories.${selected}`, selected)}
                endAdornment={
                  editablePlayer.categoria && (
                    <IconButton
                      onClick={(e) => clearSelectValue(e, 'categoria')}
                      sx={{ position: 'absolute', right: 32 }}
                      size="small"
                    >
                      <Clear fontSize="small" />
                    </IconButton>
                  )
                }
              >
                <MenuItem value="handler">{t('createPlayer.categories.handler')}</MenuItem>
                <MenuItem value="exterior">{t('createPlayer.categories.exterior')}</MenuItem>
                <MenuItem value="interior">{t('createPlayer.categories.interior')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Shooting Range */}
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>{t('createPlayer.fields.shootingRange')}</InputLabel>
              <Select
                name="shootingRange"
                value={editablePlayer.shootingRange || ''}
                onChange={handleChange}
                label={t('createPlayer.fields.shootingRange')}
                endAdornment={
                  editablePlayer.shootingRange && (
                    <IconButton
                      onClick={(e) => clearSelectValue(e, 'shootingRange')}
                      sx={{ position: 'absolute', right: 32 }}
                      size="small"
                    >
                      <Clear fontSize="small" />
                    </IconButton>
                  )
                }
              >
                <MenuItem value="Rim">{t('createPlayer.fields.rim')}</MenuItem>
                <MenuItem value="Mid-Range">{t('createPlayer.fields.midRange')}</MenuItem>
                <MenuItem value="3Pts">{t('createPlayer.fields.threePoints')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Perfil Ofensivo (MULTI SELECT) */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>{t('createPlayer.fields.offensiveProfile')}</InputLabel>
              <Select
                multiple
                name="rolOfensivo"
                value={
                  editablePlayer.rolOfensivo
                    ? editablePlayer.rolOfensivo.split(',')
                    : []
                }
                onChange={(e) => {
                  setEditablePlayer((prev) => ({
                    ...prev,
                    rolOfensivo: e.target.value.join(',')
                  }));
                }}
                renderValue={(selected) => {
                  const translatedRoles = selected.map(role => t(`createPlayer.offensiveRoles.${role}`, role));
                  const joined = translatedRoles.join(', ');
                  return (
                    <Box
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {truncateText(joined, 30)}
                    </Box>
                  );
                }}
                endAdornment={
                  editablePlayer.rolOfensivo && (
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditablePlayer((prev) => ({
                          ...prev,
                          rolOfensivo: ''
                        }));
                      }}
                      sx={{ position: 'absolute', right: 32 }}
                      size="small"
                    >
                      <Clear fontSize="small" />
                    </IconButton>
                  )
                }
              >
                <GroupHeader label="Guard" groupRoles={offensiveRoles.slice(0, 5)} currentValue={editablePlayer.rolOfensivo} field="rolOfensivo" setEditablePlayer={setEditablePlayer} />
                {offensiveRoles.slice(0, 5).map((role) => (
                  <MenuItem key={role} value={role} sx={{ pl: 4 }}>
                    <Checkbox checked={editablePlayer.rolOfensivo && editablePlayer.rolOfensivo.split(',').includes(role)} />
                    <ListItemText primary={t(`createPlayer.offensiveRoles.${role}`, role)} />
                  </MenuItem>
                ))}

                <GroupHeader label="Wing" groupRoles={offensiveRoles.slice(5, 11)} currentValue={editablePlayer.rolOfensivo} field="rolOfensivo" setEditablePlayer={setEditablePlayer} />
                {offensiveRoles.slice(5, 11).map((role) => (
                  <MenuItem key={role} value={role} sx={{ pl: 4 }}>
                    <Checkbox checked={editablePlayer.rolOfensivo && editablePlayer.rolOfensivo.split(',').includes(role)} />
                    <ListItemText primary={t(`createPlayer.offensiveRoles.${role}`, role)} />
                  </MenuItem>
                ))}

                <GroupHeader label="Forward" groupRoles={offensiveRoles.slice(11, 14)} currentValue={editablePlayer.rolOfensivo} field="rolOfensivo" setEditablePlayer={setEditablePlayer} />
                {offensiveRoles.slice(11, 14).map((role) => (
                  <MenuItem key={role} value={role} sx={{ pl: 4 }}>
                    <Checkbox checked={editablePlayer.rolOfensivo && editablePlayer.rolOfensivo.split(',').includes(role)} />
                    <ListItemText primary={t(`createPlayer.offensiveRoles.${role}`, role)} />
                  </MenuItem>
                ))}

                <GroupHeader label="Big" groupRoles={offensiveRoles.slice(14)} currentValue={editablePlayer.rolOfensivo} field="rolOfensivo" setEditablePlayer={setEditablePlayer} />
                {offensiveRoles.slice(14).map((role) => (
                  <MenuItem key={role} value={role} sx={{ pl: 4 }}>
                    <Checkbox checked={editablePlayer.rolOfensivo && editablePlayer.rolOfensivo.split(',').includes(role)} />
                    <ListItemText primary={t(`createPlayer.offensiveRoles.${role}`, role)} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Perfil Defensivo (MULTI SELECT) */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>{t('createPlayer.fields.defensiveProfile')}</InputLabel>
              <Select
                multiple
                name="rolDefensivo"
                value={
                  editablePlayer.rolDefensivo
                    ? editablePlayer.rolDefensivo.split(',')
                    : []
                }
                onChange={(e) => {
                  setEditablePlayer((prev) => ({
                    ...prev,
                    rolDefensivo: e.target.value.join(',')
                  }));
                }}
                renderValue={(selected) => {
                  const translatedRoles = selected.map(role => t(`createPlayer.defensiveRoles.${role}`, role));
                  const joined = translatedRoles.join(', ');
                  return (
                    <Box
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {truncateText(joined, 30)}
                    </Box>
                  );
                }}
                endAdornment={
                  editablePlayer.rolDefensivo && (
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditablePlayer((prev) => ({
                          ...prev,
                          rolDefensivo: ''
                        }));
                      }}
                      sx={{ position: 'absolute', right: 32 }}
                      size="small"
                    >
                      <Clear fontSize="small" />
                    </IconButton>
                  )
                }
              >
                {defensiveRoles.map((role) => (
                  <MenuItem key={role} value={role}>
                    <Checkbox
                      checked={
                        editablePlayer.rolDefensivo &&
                        editablePlayer.rolDefensivo.split(',').includes(role)
                      }
                    />
                    <ListItemText primary={t(`createPlayer.defensiveRoles.${role}`, role)} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* JE / JI */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>{t('createPlayer.fields.skillsJeJi')}</InputLabel>
              <Select
                name="jeJiType"
                value={editablePlayer.jeJiType || ''}
                onChange={(e) => {
                  setEditablePlayer((prev) => ({
                    ...prev,
                    jeJiType: e.target.value,
                    jeJiAttributes: []
                  }));
                }}
                label={t('createPlayer.fields.skillsJeJi')}
                endAdornment={
                  editablePlayer.jeJiType && (
                    <IconButton
                      onClick={(ev) => {
                        ev.stopPropagation();
                        setEditablePlayer((prev) => ({
                          ...prev,
                          jeJiType: '',
                          jeJiAttributes: []
                        }));
                      }}
                      sx={{ position: 'absolute', right: 32 }}
                      size="small"
                    >
                      <Clear fontSize="small" />
                    </IconButton>
                  )
                }
              >
                <MenuItem value="JE">{t('createPlayer.fields.jeExterior')}</MenuItem>
                <MenuItem value="JI">{t('createPlayer.fields.jiInterior')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {editablePlayer.jeJiType && (
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>
                  {`Skills ${
                    editablePlayer.jeJiType === 'JE' ? 'Exterior' : 'Interior'
                  }`}
                </InputLabel>
                <Select
                  multiple
                  value={editablePlayer.jeJiAttributes || []}
                  onChange={(e) =>
                    setEditablePlayer((prev) => ({
                      ...prev,
                      jeJiAttributes: e.target.value
                    }))
                  }
                  renderValue={(selected) => {
                    const attributeType = editablePlayer.jeJiType === 'JE' ? 'jeAttributes' : 'jiAttributes';
                    const translatedAttrs = selected.map(attr => t(`createPlayer.${attributeType}.${attr}`, attr));
                    const joined = translatedAttrs.join(', ');
                    return (
                      <Box
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {truncateText(joined, 30)}
                      </Box>
                    );
                  }}
                  endAdornment={
                    editablePlayer.jeJiAttributes?.length > 0 && (
                      <IconButton
                        onClick={(ev) => {
                          ev.stopPropagation();
                          setEditablePlayer((prev) => ({
                            ...prev,
                            jeJiAttributes: []
                          }));
                        }}
                        sx={{ position: 'absolute', right: 32 }}
                        size="small"
                      >
                        <Clear fontSize="small" />
                      </IconButton>
                    )
                  }
                >
                  {(editablePlayer.jeJiType === 'JE' ? JEAttributes : JIAttributes).map(
                    (attr) => {
                      const attributeType = editablePlayer.jeJiType === 'JE' ? 'jeAttributes' : 'jiAttributes';
                      return (
                        <MenuItem key={attr} value={attr}>
                          <Checkbox
                            checked={editablePlayer.jeJiAttributes.includes(attr)}
                          />
                          <ListItemText primary={t(`createPlayer.${attributeType}.${attr}`, attr)} />
                        </MenuItem>
                      );
                    }
                  )}
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* Current VBC Rating */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>{t('createPlayer.fields.currentVbcRating', { clubName })}</InputLabel>
              <Select
                name="nivel"
                value={editablePlayer.nivel || ''}
                onChange={handleChange}
                label={t('createPlayer.fields.currentVbcRating', { clubName })}
                renderValue={(selected) => t(`createPlayer.levels.${selected}`, selected)}
                endAdornment={
                  editablePlayer.nivel && (
                    <IconButton
                      onClick={(e) => clearSelectValue(e, 'nivel')}
                      sx={{ position: 'absolute', right: 32 }}
                      size="small"
                    >
                      <Clear fontSize="small" />
                    </IconButton>
                  )
                }
              >
                {vbcRatings.map((rating) => (
                  <MenuItem key={rating} value={rating}>
                    {t(`createPlayer.levels.${rating}`, rating)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Proyección VBC Rating */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>{t('createPlayer.fields.projectionVbcRating', { clubName })}</InputLabel>
              <Select
                name="proyeccion"
                value={editablePlayer.proyeccion || ''}
                onChange={handleChange}
                label={t('createPlayer.fields.projectionVbcRating', { clubName })}
                renderValue={(selected) => t(`createPlayer.levels.${selected}`, selected)}
                endAdornment={
                  editablePlayer.proyeccion && (
                    <IconButton
                      onClick={(e) => clearSelectValue(e, 'proyeccion')}
                      sx={{ position: 'absolute', right: 32 }}
                      size="small"
                    >
                      <Clear fontSize="small" />
                    </IconButton>
                  )
                }
              >
                {vbcRatings.map((rating) => (
                  <MenuItem key={rating} value={rating}>
                    {t(`createPlayer.levels.${rating}`, rating)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Flag Behaviour & Injury Prone */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>{t('createPlayer.fields.flagBehaviour')}</InputLabel>
              <Select
                name="comportamiento"
                value={editablePlayer.comportamiento || ''}
                onChange={handleChange}
                label={t('createPlayer.fields.flagBehaviour')}
                endAdornment={
                  editablePlayer.comportamiento && (
                    <IconButton
                      onClick={(e) => clearSelectValue(e, 'comportamiento')}
                      sx={{ position: 'absolute', right: 32 }}
                      size="small"
                    >
                      <Clear fontSize="small" />
                    </IconButton>
                  )
                }
              >
                <MenuItem value="Green">{t('createPlayer.fields.green')}</MenuItem>
                <MenuItem value="Orange">{t('createPlayer.fields.orange')}</MenuItem>
                <MenuItem value="Red">{t('createPlayer.fields.red')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>{t('createPlayer.fields.injuryProne')}</InputLabel>
              <Select
                name="injury"
                value={editablePlayer.injury || ''}
                onChange={handleChange}
                label={t('createPlayer.fields.injuryProne')}
                endAdornment={
                  editablePlayer.injury && (
                    <IconButton
                      onClick={(e) => clearSelectValue(e, 'injury')}
                      sx={{ position: 'absolute', right: 32 }}
                      size="small"
                    >
                      <Clear fontSize="small" />
                    </IconButton>
                  )
                }
              >
                <MenuItem value="Green">{t('createPlayer.fields.green')}</MenuItem>
                <MenuItem value="Orange">{t('createPlayer.fields.orange')}</MenuItem>
                <MenuItem value="Red">{t('createPlayer.fields.red')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>{t('createPlayer.fields.digitalBehaviour')}</InputLabel>
              <Select
                name="digital_behaviour"
                value={editablePlayer.digital_behaviour || ''}
                onChange={handleChange}
                label={t('createPlayer.fields.digitalBehaviour')}
                endAdornment={
                  editablePlayer.digital_behaviour && (
                    <IconButton
                      onClick={(e) => clearSelectValue(e, 'digital_behaviour')}
                      sx={{ position: 'absolute', right: 32 }}
                      size="small"
                    >
                      <Clear fontSize="small" />
                    </IconButton>
                  )
                }
              >
                <MenuItem value="Green">{t('createPlayer.fields.green')}</MenuItem>
                <MenuItem value="Orange">{t('createPlayer.fields.orange')}</MenuItem>
                <MenuItem value="Red">{t('createPlayer.fields.red')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Comparación Pro */}
          <Grid item xs={12}>
            <TextField
              label={t('createPlayer.fields.proComparison')}
              name="comparacionPro"
              value={editablePlayer.comparacionPro || ''}
              onChange={handleChange}
              fullWidth
            />
          </Grid>
        </Grid>
      ) : (
        // READ ONLY (DESKTOP)
        <Box sx={{ mt: 0.5 }}>
          <Grid container spacing={1.5}>
            {!!player.poste && (() => {
              const positionMap = {
                'Base': t('positions.pointGuard'),
                'Escolta': t('positions.shootingGuard'),
                'Alero': t('positions.smallForward'),
                'Ala-Pívot': t('positions.powerForward'),
                'Ala-Pivot': t('positions.powerForward'),
                'Pívot': t('positions.center'),
                'Pivot': t('positions.center')
              };
              const translatedPosition = positionMap[player.poste] || player.poste;
              return (
                <Grid item xs={12} sm={6} md={4}>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.03em'
                      }}
                    >
                      {t('createPlayer.fields.position')}:
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '0.95rem',
                        color: 'text.primary',
                        fontWeight: 400
                      }}
                    >
                      {translatedPosition}
                    </Typography>
                  </Box>
                </Grid>
              );
            })()}
            {!!player.mano && (() => {
              // Support both old Spanish values and new translation keys
              const handMap = {
                'Derecha': t('createPlayer.fields.rightHand'),
                'Izquierda': t('createPlayer.fields.leftHand'),
                'Ambas': t('createPlayer.fields.bothHands'),
                'rightHand': t('createPlayer.fields.rightHand'),
                'leftHand': t('createPlayer.fields.leftHand'),
                'bothHands': t('createPlayer.fields.bothHands')
              };
              const translatedHand = handMap[player.mano] || player.mano;
              return (
                <Grid item xs={12} sm={6} md={4}>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.03em'
                      }}
                    >
                      {t('createPlayer.fields.dominantHand')}:
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '0.95rem',
                        color: 'text.primary',
                        fontWeight: 400
                      }}
                    >
                      {translatedHand}
                    </Typography>
                  </Box>
                </Grid>
              );
            })()}
          {!!player.categoria && (() => {
            // Support both old Spanish values and new translation keys
            const categoryMap = {
              'Manejador': t('createPlayer.categories.handler'),
              'Exterior': t('createPlayer.categories.exterior'),
              'Interior': t('createPlayer.categories.interior'),
              'handler': t('createPlayer.categories.handler'),
              'exterior': t('createPlayer.categories.exterior'),
              'interior': t('createPlayer.categories.interior')
            };
            const translatedCategory = categoryMap[player.categoria] || player.categoria;
            return (
              <Grid item xs={12} sm={6} md={4}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.03em'
                    }}
                  >
                    {t('createPlayer.fields.category')}:
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.95rem',
                      color: 'text.primary',
                      fontWeight: 400
                    }}
                  >
                    {translatedCategory}
                  </Typography>
                </Box>
              </Grid>
            );
          })()}
          {!!player.shootingRange && (() => {
            const shootingRangeMap = {
              'Rim': t('createPlayer.fields.rim'),
              'Mid-Range': t('createPlayer.fields.midRange'),
              '3Pts': t('createPlayer.fields.threePoints')
            };
            const translatedRange = shootingRangeMap[player.shootingRange] || player.shootingRange;
            return (
              <Grid item xs={12} sm={6} md={4}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.03em'
                    }}
                  >
                    {t('createPlayer.fields.shootingRange')}:
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.95rem',
                      color: 'text.primary',
                      fontWeight: 400
                    }}
                  >
                    {translatedRange}
                  </Typography>
                </Box>
              </Grid>
            );
          })()}

          {!!player.rolOfensivo && (() => {
            const roles = player.rolOfensivo.split(',').map(role => role.trim());
            const translatedRoles = roles.map(role => t(`createPlayer.offensiveRoles.${role}`, role)).join(', ');
            return (
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.03em'
                    }}
                  >
                    {t('createPlayer.fields.offensiveProfile')}:
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.95rem',
                      color: 'text.primary',
                      fontWeight: 400
                    }}
                  >
                    {translatedRoles}
                  </Typography>
                </Box>
              </Grid>
            );
          })()}
          {!!player.rolDefensivo && (() => {
            const roles = player.rolDefensivo.split(',').map(role => role.trim());
            const translatedRoles = roles.map(role => t(`createPlayer.defensiveRoles.${role}`, role)).join(', ');
            return (
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.03em'
                    }}
                  >
                    {t('createPlayer.fields.defensiveProfile')}:
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.95rem',
                      color: 'text.primary',
                      fontWeight: 400
                    }}
                  >
                    {translatedRoles}
                  </Typography>
                </Box>
              </Grid>
            );
          })()}

          {!!player.jeJiType && (
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.03em'
                  }}
                >
                  {t('createPlayer.fields.skillsJeJi')}:
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.95rem',
                    color: 'text.primary',
                    fontWeight: 400
                  }}
                >
                  {player.jeJiType}
                </Typography>
              </Box>
            </Grid>
          )}
          {!!player.jeJiAttributes?.length && (() => {
            const attributeType = player.jeJiType === 'JE' ? 'jeAttributes' : 'jiAttributes';
            const translatedAttributes = player.jeJiAttributes.map(attr =>
              t(`createPlayer.${attributeType}.${attr}`, attr)
            ).join(', ');
            return (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.03em'
                    }}
                  >
                    Skills ({player.jeJiType}):
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.95rem',
                      color: 'text.primary',
                      fontWeight: 400
                    }}
                  >
                    {translatedAttributes}
                  </Typography>
                </Box>
              </Grid>
            );
          })()}

          {!!player.nivel && (() => {
            const translatedLevel = t(`createPlayer.levels.${player.nivel}`, player.nivel);
            return (
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.03em'
                    }}
                  >
                    {t('createPlayer.fields.currentVbcRating', { clubName })}:
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.95rem',
                      color: 'text.primary',
                      fontWeight: 400
                    }}
                  >
                    {translatedLevel}
                  </Typography>
                </Box>
              </Grid>
            );
          })()}
          {!!player.proyeccion && (() => {
            const translatedLevel = t(`createPlayer.levels.${player.proyeccion}`, player.proyeccion);
            return (
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.03em'
                    }}
                  >
                    {t('createPlayer.fields.projectionVbcRating', { clubName })}:
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.95rem',
                      color: 'text.primary',
                      fontWeight: 400
                    }}
                  >
                    {translatedLevel}
                  </Typography>
                </Box>
              </Grid>
            );
          })()}

          {!!player.comportamiento && (() => {
            const flagMap = {
              'Green': t('createPlayer.fields.green'),
              'Orange': t('createPlayer.fields.orange'),
              'Red': t('createPlayer.fields.red')
            };
            const translatedFlag = flagMap[player.comportamiento] || player.comportamiento;
            return (
              <Grid item xs={12} sm={6} md={4}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.03em'
                    }}
                  >
                    {t('createPlayer.fields.flagBehaviour')}:
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.95rem',
                      color: 'text.primary',
                      fontWeight: 400
                    }}
                  >
                    {translatedFlag}
                  </Typography>
                </Box>
              </Grid>
            );
          })()}
          {!!player.injury && (() => {
            const flagMap = {
              'Green': t('createPlayer.fields.green'),
              'Orange': t('createPlayer.fields.orange'),
              'Red': t('createPlayer.fields.red')
            };
            const translatedFlag = flagMap[player.injury] || player.injury;
            return (
              <Grid item xs={12} sm={6} md={4}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.03em'
                    }}
                  >
                    {t('createPlayer.fields.injuryProne')}:
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.95rem',
                      color: 'text.primary',
                      fontWeight: 400
                    }}
                  >
                    {translatedFlag}
                  </Typography>
                </Box>
              </Grid>
            );
          })()}
          {!!player.digital_behaviour && (() => {
            const flagMap = {
              'Green': t('createPlayer.fields.green'),
              'Orange': t('createPlayer.fields.orange'),
              'Red': t('createPlayer.fields.red')
            };
            const translatedFlag = flagMap[player.digital_behaviour] || player.digital_behaviour;
            return (
              <Grid item xs={12} sm={6} md={4}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.03em'
                    }}
                  >
                    {t('createPlayer.fields.digitalBehaviour')}:
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.95rem',
                      color: 'text.primary',
                      fontWeight: 400
                    }}
                  >
                    {translatedFlag}
                  </Typography>
                </Box>
              </Grid>
            );
          })()}

          {!!player.comparacionPro && (
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.03em'
                  }}
                >
                  {t('createPlayer.fields.proComparison')}:
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.95rem',
                    color: 'text.primary',
                    fontWeight: 400
                  }}
                >
                  {player.comparacionPro}
                </Typography>
              </Box>
            </Grid>
            )}
          </Grid>
        </Box>
      )}
    </Paper>
  );
}

export default CaracteristicasJugador;
